import { RefObject, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Fade, Popover } from '@mui/material';
import { Clock4, Tag, UserRound } from 'lucide-react';

import { DropdownMenu, PlayTagDisplay } from '@labradorsports/components';
import { PlaybookTabs, ShareLevels } from '@labradorsports/constants';
import { countDisplay, getAllowedPlayOperations, noPropagation } from '@labradorsports/utils';

import { ContentViewer } from '../../shared/form/index.js';
import { useNavigateWithQuery } from '../../shared/providers/index.js';
import { SafeDateFormat } from '../../shared/structural/index.js';
import {
    Selectors,
    useShiftPlaybookItemsMutation,
    useShareItemsMutation,
    ShareConfig,
    useAllTeamsMembersQuery,
} from '../../store/index.js';
import TeamItemsDropdown from '../team-items-dropdown/team-items-dropdown.js';

interface Props {
    play: any;
    readOnly: boolean;
    onClick: AnyFunction;
    moveToFolder?: AnyFunction;
    deleteClick?: AnyFunction;
    unshareClick?: AnyFunction;
    playbookType: string;
    copyPlay?: AnyFunction;
    hideDropdown?: boolean;
    onRename?: AnyFunction;
    getMenuPosition?: (menuHeight: number) => 'bottom' | 'top';
    dropdownMenuRef?: RefObject<HTMLDivElement>;
    startShare?: (shareConfig?: ShareConfig) => void;
    viewed: boolean;
}

const PlayListPlay: FC<Props> = ({
    play,
    readOnly,
    onClick,
    deleteClick,
    unshareClick,
    copyPlay,
    moveToFolder,
    playbookType,
    hideDropdown,
    onRename,
    getMenuPosition,
    dropdownMenuRef,
    startShare,
    viewed,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [shareItems] = useShareItemsMutation();
    const [movePlaybookItem] = useShiftPlaybookItemsMutation();
    const activeTeam = useSelector(Selectors.activeTeam);
    const team = useSelector(Selectors.currentTeam);
    const teamRole = useSelector(Selectors.currentUserTeamRole);
    const { data: teamMembers } = useAllTeamsMembersQuery();
    const isTeamPlaybook = playbookType === PlaybookTabs.TEAM;

    const [hovered, setHovered] = useState(false);
    const [tagsExpanded, setTagsExpanded] = useState(false);
    const navigate = useNavigateWithQuery();

    const onHover = () => {
        setHovered(true);
    };

    const onHoverLeave = () => {
        setHovered(false);
    };

    useEffect(() => {
        const hideDetails = () => setHovered(false);
        window.addEventListener('dropdown-opened', hideDetails);
        return () => window.removeEventListener('dropdown-opened', hideDetails);
    }, []);

    const _onClick = (evt: any) => {
        // Ignore clicks from the dropdown menu
        if (!evt.target.closest('.dropdown-panel')) {
            onClick?.();
        }

        onHoverLeave();
    };

    const showViewerDetailsClick = noPropagation(() => {
        navigate('/playbook/usage', {
            viewerDetailsPlayId: play.id,
        });
    });

    const sharePlayClick = () => {
        startShare?.({
            itemIds: [play.id],
        });
    };

    const setShareAudience = (audience: string) => {
        shareItems({
            srcPlaybookId: play.playbookId,
            itemIds: [play.id],
            modifications: {
                [activeTeam]: audience,
            },
        });
    };

    const moveItem = (up: boolean) => {
        movePlaybookItem({
            itemIds: [play.id],
            up,
            playbookType,
        });
    };

    const allowedTo = getAllowedPlayOperations(team, play, readOnly, playbookType, teamRole);

    const menuOptions: {
        text: string;
        onClick?: () => any;
        show: boolean;
    }[] = [
        {
            text: 'Rename',
            onClick: onRename,
            show: allowedTo.RENAME && Boolean(onRename),
        },
        {
            text: 'Move to Folder',
            onClick: moveToFolder,
            show: allowedTo.MOVE && Boolean(moveToFolder),
        },
        {
            text: 'Share',
            onClick: sharePlayClick,
            show: allowedTo.SHARE && Boolean(startShare),
        },
        {
            text: 'Delete',
            onClick: deleteClick,
            // TeamItemsDropdown handles remove button
            show: allowedTo.DELETE && !isTeamPlaybook && Boolean(deleteClick),
        },
        {
            text: 'Copy',
            onClick: copyPlay,
            show: allowedTo.COPY && Boolean(copyPlay),
        },
        {
            text: 'Move Up',
            onClick: () => moveItem(true),
            show: allowedTo.MOVE,
        },
        {
            text: 'Move Down',
            onClick: () => moveItem(false),
            show: allowedTo.MOVE,
        },
    ].filter(({ show }) => show);

    const toggleShowTags = noPropagation(() => setTagsExpanded(!tagsExpanded));

    const playName = `${play.coachOnly ? '* ' : ''}${play.name ?? 'New Play'}`;

    const classNames = ['play-list-play', 'list-item-content'];

    if (!isTeamPlaybook || viewed) {
        classNames.push('viewed');
    }

    if (play.hidden) {
        classNames.push('blurred');
    }

    const sharedBy = teamMembers?.[play.sharedBy];

    const visibleTags: string[] = Array.from(
        new Set(play.tags?.filter((tag: string) => !play.formationTags?.includes(tag)))
    );

    return (
        <div
            className={classNames.join(' ')}
            onClick={!play.hidden ? _onClick : null}
            ref={ref}
            onMouseEnter={onHover}
            onMouseLeave={onHoverLeave}
        >
            <div className="play-name-container">
                <span className="play-name">{playName}</span>
                <div className={`play-tag-collapse ${tagsExpanded ? 'expanded' : ''}`}>
                    <PlayTagDisplay tags={visibleTags} />
                    {visibleTags.length > 2 ? (
                        <Button className="see-all-tags-button" onClick={toggleShowTags}>
                            {tagsExpanded ? 'Hide' : 'See All'}
                        </Button>
                    ) : null}
                </div>
                {readOnly || !isTeamPlaybook ? undefined : (
                    <div className="viewer-count">
                        <Button onClick={showViewerDetailsClick}>
                            {countDisplay('Viewer', play.viewerCount ?? 0)}
                        </Button>
                    </div>
                )}
            </div>
            <Popover
                open={hovered}
                onClose={onHoverLeave}
                className="d-none d-md-block"
                disableScrollLock
                disableAutoFocus
                slotProps={{
                    paper: {
                        className: 'play-hover-details',
                    },
                }}
                sx={{ pointerEvents: 'none', zIndex: 800 }}
                anchorEl={ref.current}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transitionDuration={100}
                slots={{
                    transition: Fade,
                }}
            >
                <h2>{play.name}</h2>
                {play.description ? <ContentViewer value={play.description} /> : null}
                {isTeamPlaybook && sharedBy ? (
                    <div>
                        <span className="text-muted">
                            <UserRound size={14} className="me-2" />
                            Shared by
                        </span>{' '}
                        <em>
                            {sharedBy.firstName} {sharedBy.lastName}
                        </em>
                    </div>
                ) : null}
                <div>
                    <span className="text-muted">
                        <Clock4 size={14} className="me-2" />
                        Last Updated
                    </span>{' '}
                    <em>
                        <SafeDateFormat date={play.lastUpdated} pattern="MMMM d, yyyy" />
                    </em>
                </div>
                {visibleTags.length > 0 ? (
                    <div>
                        <div className="text-muted mb-2">
                            <Tag size={14} className="me-2" />
                            Tags
                        </div>
                        <div className="play-tags">
                            {visibleTags.map((tag: string) => (
                                <span className="play-tag" key={tag}>
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : null}
            </Popover>

            {menuOptions.length > 0 && !play.hidden && !hideDropdown ? (
                <DropdownMenu
                    options={menuOptions}
                    getMenuPosition={getMenuPosition}
                    DropdownComponent={isTeamPlaybook && !readOnly ? TeamItemsDropdown : null}
                    containerRef={dropdownMenuRef}
                    dropdownProps={{
                        allowedTo,
                        items: [play],
                        shareAudience: play.coachOnly ? ShareLevels.COACHES : ShareLevels.TEAM,
                        deleteClick,
                        unshareClick,
                        edit: onClick,
                        setShareAudience,
                    }}
                />
            ) : null}
        </div>
    );
};

export default PlayListPlay;
